import { db } from '@/lib/db';
import { checkAchievements, getAchievementDef } from '@/lib/achievements/catalog';
import type { RoundForAchievement } from '@/lib/achievements/catalog';

export interface UnlockedAchievement {
  achievement_id: string;
  name: string;
  icon: string;
  desc: string;
}

export async function processAchievementsAfterRound(roundId?: string): Promise<UnlockedAchievement[]> {
  const me = await db.auth.me();
  if (!me) return [];

  const [posts, acesList, existing, courses] = await Promise.all([
    db.posts.list({ limit: 200, authorId: me.id }),
    db.aces.forMember(me.id),
    db.achievements.list(me.id),
    db.courses.list(),
  ]);

  const courseMap = Object.fromEntries(courses.map((c) => [c.id, c]));
  const rounds: RoundForAchievement[] = [];

  for (const p of posts) {
    if (p.type !== 'round' || !p.round) continue;
    const course = p.round.course_id ? courseMap[p.round.course_id] : null;
    const holeScores = (p.round.round_holes || []).map((h) => {
      const holeDef = (p.round?.course_holes || []).find((ch) => ch.hole_num === h.hole_num);
      return { hole_num: h.hole_num, strokes: h.strokes, par: holeDef?.par ?? 4 };
    });
    rounds.push({
      member_id: me.id,
      gross_total: p.round.gross_total,
      holes: p.round.holes,
      course_slug: course?.slug ?? null,
      par: course?.par ?? 72,
      hole_scores: holeScores,
      posted_handicap: p.round.posted_handicap,
      played_at: p.round.played_at,
    });
  }

  const aceInputs = acesList.map((a) => ({ member_id: me.id, played_at: a.played_at }));
  const already = existing.map((a) => ({ achievement_id: a.achievement_id, year: a.year }));
  const newIds = checkAchievements(me.id, rounds, aceInputs, already);

  const unlocked: UnlockedAchievement[] = [];
  for (const id of newIds) {
    const row = await db.achievements.unlock(id, roundId);
    if (!row) continue;
    const def = getAchievementDef(id);
    await db.posts.create({
      type: 'achievement',
      achievement_id: row.id,
      body: def ? `${def.icon} ${def.name}` : id,
    });
    unlocked.push({
      achievement_id: id,
      name: def?.name ?? id,
      icon: def?.icon ?? '🏆',
      desc: def?.desc ?? '',
    });
  }
  return unlocked;
}
